import { useRef, useState } from "react";
import { hideOnError, imgSrc } from "@/components/ImageSlot";

const clamp = (n) => Math.min(100, Math.max(0, n));

// Interactive before/after comparison. Both photos must show the same
// property from the same position; they resolve from /images/<id>.
export const BeforeAfterSlider = ({ id, before, after, caption, featured = false }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);
  const beforeSrc = before.src || imgSrc(before.id);
  const afterSrc = after.src || imgSrc(after.id);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(clamp(((clientX - rect.left) / rect.width) * 100));
  };
  const onPointerDown = (e) => {
    dragging.current = true;
    ref.current.setPointerCapture(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => dragging.current && update(e.clientX);
  const stop = () => {
    dragging.current = false;
  };
  const onKeyDown = (e) => {
    const step = e.shiftKey ? 10 : 2;
    const map = { ArrowLeft: -step, ArrowDown: -step, ArrowRight: step, ArrowUp: step };
    if (e.key in map) {
      e.preventDefault();
      setPos((p) => clamp(p + map[e.key]));
    } else if (e.key === "Home") {
      e.preventDefault();
      setPos(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setPos(100);
    }
  };

  return (
    <figure data-testid={`before-after-${id}`} data-featured={featured}>
      <div
        ref={ref}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={stop}
        onPointerCancel={stop}
        className="relative aspect-[3/2] cursor-ew-resize select-none overflow-hidden rounded-card bg-alt"
        style={{ touchAction: "pan-y" }}
      >
        <div data-slot={after.id} className="absolute inset-0">
          <img src={afterSrc} alt={after.alt} width="1200" height="800" loading="lazy" decoding="async" draggable={false} onError={hideOnError} className="h-full w-full object-cover" />
        </div>
        <div data-slot={before.id} className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <img src={beforeSrc} alt={before.alt} width="1200" height="800" loading="lazy" decoding="async" draggable={false} onError={hideOnError} className="h-full w-full object-cover" />
        </div>
        <span aria-hidden="true" className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-navy/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-white">
          Before
        </span>
        <span aria-hidden="true" className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-navy/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-white">
          After
        </span>
        <div aria-hidden="true" className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white" style={{ left: `${pos}%` }} />
        <button
          type="button"
          role="slider"
          data-testid={`before-after-handle-${id}`}
          aria-label={`Compare before and after: ${caption}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
          aria-valuetext={`${Math.round(pos)}% before`}
          onKeyDown={onKeyDown}
          className="absolute top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-navy text-white shadow-soft transition-colors duration-200 hover:bg-brand"
          style={{ left: `${pos}%`, touchAction: "none" }}
        >
          <span aria-hidden="true" className="block h-4 w-0.5 rounded-full bg-white" />
        </button>
      </div>
      <figcaption data-testid={`before-after-caption-${id}`} className="mt-4 text-base font-semibold text-navy">
        {caption}
      </figcaption>
    </figure>
  );
};
